import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { LocalStorageService } from 'ngx-webstorage';
import { ServiceConfig } from "../../core/core.constants";
import { UserCredentials } from '../../model/user-credentials';
import { tap, catchError, filter, take, map, mergeMap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { _throw } from 'rxjs/observable/throw';
import { of } from 'rxjs/observable/of';

@Injectable()
export class AuthServerProvider {
    constructor(
        private http: HttpClient,
        private $localStorage: LocalStorageService
    ) { }

    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<() => Observable<string>> = new BehaviorSubject<() => Observable<string>>(null);

    getToken(): string {
        return this.$localStorage.retrieve(this.authenticationTokenConstant);
    }

    private getRefreshToken(): string {
        return this.$localStorage.retrieve(this.refreshTokenConstant);
    }

    login(credentials: UserCredentials): Observable<any> {

        return this.httpPost('AuthenticateUser', credentials)
            .pipe(
                tap(response => this.storeTokens(response.JwtToken, response.RefreshToken)));
    }

    getAccount(): Observable<Nema.Crew.Model.ApplicationUser> {
        return this.httpGet('CurrentUser');
    }

    changePassword(dto: ChangePasswordDto): Observable<any> {
        return this.httpPost('ChangePassword', dto);
    }

    getPasswordResetInfo(dto: GetPasswordResetInfoDto): Observable<PasswordResetInfo> {
        return this.httpGet<PasswordResetInfo>('GetPasswordResetInfo', { ...dto });
    }

    sendPasswordResetLink(dto: SendPasswordResetLinkDto): Observable<any> {
        return this.httpPost('SendPasswordResetLink', dto);
    }

    resetPassword(dto: ResetPasswordDto): Observable<any> {
        return this.httpPost('ResetPassword', dto);
    }

    private storeTokens(jwt: string, refreshToken: string): void {
        this.storeAuthenticationToken(jwt);
        this.storeRefreshToken(refreshToken);
    }

    private storeAuthenticationToken(jwt: string): void {
        this.$localStorage.store(this.authenticationTokenConstant, jwt);
    }

    private storeRefreshToken(refreshToken: string): void {
        this.$localStorage.store(this.refreshTokenConstant, refreshToken);
    }

    logout(): Observable<any> {

        return new Observable((observer) => {
            this.$localStorage.clear(this.authenticationTokenConstant);
            this.$localStorage.clear(this.refreshTokenConstant);
            observer.complete();
        });
    }

    refreshToken(): Observable<string> {
        if (!this.isRefreshing) {
            return this.refresh();
        }
        else {
            return this.waitForRefresh();
        }
    }

    private refresh(): Observable<string> {
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        const jwtToken = this.getToken();
        const refreshToken = this.getRefreshToken();

        console.info("jwtToken: ", jwtToken);
        console.info("refreshToken: ", refreshToken);

        return this.httpPost('RefreshToken', { JwtToken: jwtToken, RefreshToken: refreshToken })
            .pipe(
                map((response: any) => {
                    const newJwtToken: string = response.JwtToken;
                    this.storeTokens(newJwtToken, response.RefreshToken);
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(() => of(newJwtToken));
                    return newJwtToken;
                }),
                catchError(error => {
                    console.info("RefreshToken call failed: ", error);
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(() => _throw(error));
                    return _throw(error);
                }));
    }

    private waitForRefresh(): Observable<string> {
        return this.refreshTokenSubject.pipe(
            filter(func => func != null),
            take(1),
            mergeMap(func => func()));
    }

    private httpGet<T = any>(endpoint: string, params?: HttpParams | { [param: string]: string | string[] }): Observable<T> {
        return this.http.get<T>(AuthServerProvider.getUrl(endpoint), { params: params });
    }

    private httpPost<T = any>(endpoint: string, body: any): Observable<T> {
        return this.http.post<T>(AuthServerProvider.getUrl(endpoint), body);
    }

    private static getUrl(endpoint: string): string {
        return ServiceConfig.webApiUrl + 'ApplicationUserService/' + endpoint;
    }

    private readonly authenticationTokenConstant = 'authenticationToken';

    private readonly refreshTokenConstant = 'refreshToken';
}

export interface ChangePasswordDto {
    OldPassword: string;
    NewPassword: string;
}

export interface GetPasswordResetInfoDto {
    Email: string;
    Company: string;
}

export interface PasswordResetInfo {
    UserId: string;
    Token: string;
}

export interface SendPasswordResetLinkDto {
    UserId: string;
    Company: string;
    ResetUrl: string;
}

export interface ResetPasswordDto {
    UserId: string;
    Company: string;
    Token: string;
    Password: string;
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/shared/auth/auth.service.ts